import { currencies } from "country-data";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import {
  AlertCircle,
  BarChart2,
  Book,
  Briefcase,
  CheckCircle,
  Clipboard,
  CreditCard,
  Database,
  Edit,
  File,
  FileText,
  Globe,
  HelpCircle,
  Layers,
  Layout,
  List,
  MessageSquare,
  Package,
  PlusSquare,
  Settings,
  Target,
  Upload,
  User,
  UserCheck,
  Users,
} from "react-feather";
import { BiNetworkChart, BiReceipt, BiUserCircle } from "react-icons/bi";
import {
  FaCheckCircle,
  FaClock,
  FaFileSignature,
  FaHeadset,
  FaMoneyCheckAlt,
  FaNetworkWired,
  FaTimesCircle,
  FaTruck,
  FaUserCheck,
} from "react-icons/fa";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiXCircle,
} from "react-icons/fi";
import { IoPricetag } from "react-icons/io5";
import { TbShieldQuestion } from "react-icons/tb";
import { toast } from "react-toastify";
import { CommonEnums, Paths, UserRoles } from "../constants/enums";
import { logoutUser } from "../services/authService";
import formatDate from "./formatDate";
import { formatCurrency } from "./Formatters";
const ROLE_CLAIM =
  "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";

export const getToken = () => Cookies.get("accessTokenJKT");
export const getRefreshToken = () => Cookies.get("refreshTokenJKT");
export const setTokens = (accessToken, refreshToken) => {
  Cookies.set("accessTokenJKT", accessToken, {
    expires: 1,
    secure: true,
    sameSite: "Strict",
  });
  Cookies.set("refreshTokenJKT", refreshToken, {
    expires: 7,
    secure: true,
    sameSite: "Strict",
  });
};

// Clear Tokens
export const clearTokens = () => {
  Cookies.remove("accessTokenJKT");
  Cookies.remove("refreshTokenJKT");
};

export const getUserFromToken = () => {
  const token = getToken();
  if (!token) return null;

  try {
    const decoded = jwtDecode(token);
    return {
      id: decoded.Id || null,
      role: decoded[ROLE_CLAIM],
      username: decoded.UserName,
    };
  } catch (error) {
    console.error("Invalid token:", error);
    logoutUser();
    return null;
  }
};

export const getUserRole = () => {
  const token = getToken();
  if (!token) return null;

  const decoded = jwtDecode(token);

  return decoded.Role;
};

export const handleApiSuccess = (method, endpoint) => {
  let message = "Operation successful!"; // Default message

  const endpointMessages = {
    // Login
    "/api/Login/login": "Logged in successfully!",
    "/api/Login/logoutid": "Logged out successfully!",

    // User
    "/User/add": "User added successfully!",

    // Customer
    "/api/Customer/add": "Customer added successfully!",
    "/api/Customer/customer-types": "Customer type operation successful!",
    "/api/Customer/customer-region": "Customer region operation successful!",

    // Approval Flow
    "/api/ApprovalFlow/approval-types": "Approval type operation successful!",
    "/api/ApprovalFlow/multilevel-approvals":
      "Multilevel approval operation successful!",
    "/api/ApprovalFlow/customerhierarchy":
      "Customer hierarchy operation successful!",
    "/api/ApprovalFlow/business-units": "Business unit operation successful!",
    "/api/ApprovalFlow": "Approval flow operation successful!",

    // CompanyDetails
    "/api/CompanyDetails/security-groups-name":
      "Security group operation successful!",
    "/api/CompanyDetails/departments": "Department operation successful!",
    "/api/CompanyDetails/designation": "Designation operation successful!",

    // Logistic Details
    "/api/LogisticDetails/logistic": "Logistic info added successfully!",
    "/api/LogisticDetails": "Logistic info operation successful!",

    // Disputes
    "/api/Dispute/dispute": "Dispute submitted successfully!",
    "/api/Dispute/updatedispute": "Dispute updated successfully!",
    "/api/Dispute/deletedispute": "Dispute deleted successfully!",

    // Dispute Categories
    "/api/DisputeCategories": "Dispute category operation successful!",

    // Notifications
    "/api/Notifications": "Notification operation successful!",

    // Privileges
    "/api/Privilege/privilege-types": "Privilege type operation successful!",
    "/api/Privilege/user-privileges": "User privilege operation successful!",

    // CFDI Invoice
    "/api/CfdiInvoice/Cfdi_Details": "CFDI details fetched successfully!",

    // SAP
    "/api/Sap/ExistingCustomersFromSAP":
      "Existing customers imported from SAP!",
    "/api/Sap/CreditNoteFromSAP": "Credit note imported from SAP!",
    "/api/Sap/CustomerInvoiceFromSAP": "Invoice imported from SAP!",

    // Credit Notes
    "/api/CreditNotes": "Credit notes fetched successfully!",

    // Others
    "/api/AllCustomersList/AllCustomerNameList": "Customer list retrieved!",
    "/api/InvoiceDetails/InvoiceDetails":
      "Invoice details fetched successfully!",
    "/api/CustomerPayment": "Customer payment data retrieved!",
    "/api/CustomerAccountStatement": "Customer account statement retrieved!",
    "/api/CustomerDashboard": "Customer dashboard loaded!",
    "/api/CreditLine/creditline/create-credit-line":
      "Credit notes created successfully!",
    "/api/CreditLine/update/": "Credit notes updated successfully!",
    "/ApprovalFlow/delete-cust-subregion":
      "Customer Sub Region Deleted Successfully",
    "/User/save-customer-access": "Customer Access Created Successfully",
    "/User/delete-all-customer-access": "Customer Access Deleted Successfully",
  };

  // Determine specific message
  for (let key in endpointMessages) {
    if (endpoint.includes(key)) {
      message = endpointMessages[key];
      break;
    }
  }

  if (message === "Operation successful!") return;

  // Adjust messages based on HTTP method
  switch (method.toUpperCase()) {
    case "POST":
      toast.success(message);
      break;
    case "PUT":
    case "PATCH":
      toast.success(
        message.replace("added", "updated").replace("created", "updated")
      );
      break;
    default:
      break;
  }
};

/**
 * Decodes a JWT token and returns its payload along with helper metadata.
 *
 * @param {string} token - The JWT token string
 * @returns {object|null} - Decoded payload + isExpired flag, or null on failure
 */
export const decodeJWT = (token) => {
  if (!token || typeof token !== "string") return {};

  try {
    const decoded = jwtDecode(token);
    const exp = decoded?.exp;

    return {
      ...decoded,
      isExpired: exp ? Date.now() >= exp * 1000 : false,
    };
  } catch (error) {
    console.error("Invalid JWT:", error);
    return null;
  }
};

// utils/form.js

// Parse path string like "creditLine.commercialReferences[2].relationshipType" into keys
function parsePath(path) {
  // Split by dot, but also separate array indexes
  const parts = [];

  path.split(".").forEach((part) => {
    const arrayRegex = /([^\[\]]+)|\[(\d+)\]/g;
    let match;
    while ((match = arrayRegex.exec(part)) !== null) {
      // match[1] is a key name, match[2] is an index if exists
      if (match[1]) {
        parts.push(match[1]);
      }
      if (match[2]) {
        parts.push(Number(match[2]));
      }
    }
  });

  return parts;
}

// Deep get supporting array indexes
export function getDeep(obj, path) {
  const keys = parsePath(path);
  return keys.reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
}

// Get error only if touched and error exist
export function getFieldError(fieldName, touched, errors) {
  const isTouched = getDeep(touched, fieldName);
  const error = getDeep(errors, fieldName);
  return isTouched && error ? error : null;
}

export const getMonth = (month) => {
  switch (month) {
    case "01":
      return "ENERO";
    case "02":
      return "FEBRERO";
    case "03":
      return "MARZO";
    case "04":
      return "ABRIL";
    case "05":
      return "MAYO";
    case "06":
      return "JUNIO";
    case "07":
      return "JULIO";
    case "08":
      return "AGOSTO";
    case "09":
      return "SEPTIEMBRE";
    case "10":
      return "OCTUBRE";
    case "11":
      return "NOVIEMBRE";
    case "12":
      return "DICIEMBRE";
    default:
      return "MES INVÁLIDO";
  }
};

export const flattenValues = (values, parentKey = "", res = {}) => {
  for (let key in values) {
    const value = values[key];
    const newKey = parentKey ? `${parentKey}.${key}` : key;

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (typeof item === "object" && item !== null) {
          flattenValues(item, `${newKey}[${index}]`, res);
        } else {
          res[`${newKey}[${index}]`] = item;
        }
      });
    } else if (typeof value === "object" && value !== null) {
      flattenValues(value, newKey, res);
    } else {
      res[newKey] = value;
    }
  }
  return res;
};

// mergeReferencesIntoPayments removed

export function getNestedValue(obj, path) {
  if (!obj || typeof obj !== "object" || !path) return true;

  const keys = path.split(".");
  let current = obj;

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];

    if (current === null || typeof current !== "object" || !(key in current)) {
      return true; // path doesn't exist
    }

    current = current[key];

    if (i === keys.length - 1) {
      return !current; // return true if final value is falsy
    }
  }

  return true; // fallback
}

// utils/hasAnyNestedError.js

/**
 * Checks if any of the given nested field paths exist in the object and have a truthy value.
 *
 * @param {object} obj - The object to search in.
 * @param {string[]} paths - Array of dot-notated field paths (e.g. 'a.b[0].c' or 'a.b.0.c').
 * @returns {boolean} True if any field path resolves to a truthy value, false otherwise.
 */
export function hasAnyNestedError(obj, paths) {
  if (!obj || typeof obj !== "object" || !Array.isArray(paths)) return false;

  const getNestedValue = (target, path) => {
    return path.split(".").reduce((acc, key) => {
      if (acc === undefined || acc === null) return undefined;

      const match =
        key.match(/^(\w+)\[(\d+)\]$/) || key.match(/^(\w+)\.(\d+)$/);
      if (match) {
        const [, arrKey, index] = match;
        return acc?.[arrKey]?.[index];
      }

      return acc?.[key];
    }, target);
  };

  return paths.some((path) => !!getNestedValue(obj, path));
}

export const getDocuments = (t, customerRegion, customerCategory) => {
  if (customerRegion === "1") {
    return customerCategory === CommonEnums.INDIVIDUAL
      ? [
        {
          label: "Official Id (Ine, Passport, Actual)",
          label: t("domesticcustomer.OfficialId"),
          name: "Cust_Official_Id",
          required: true,
          id: 9,
        },
        {
          label:
            "Address Proof(Official, Less Than 3 Months Of Aging), Any Of Services Invoices (Utility Bills), Bank Statement)",
          label: t("domesticcustomer.AddressProof"),
          name: "Cust_Address_Proof",
          id: 10,
          required: true,
        },
        {
          label:
            "Bank statement of accout (only the Customer data not the details of movement and also customer can erase balance) Credit Limit request time",
          label: t("domesticcustomer.Bankstatementofaccount"),
          name: "Cust_Bank_statement",
          id: 15,
          required: true,
        },
        {
          label: "Tax ID Document",
          label: t("domesticcustomer.taxiddocument"),
          name: "Cust_Tax_ID_document",
          id: 16,
          required: true,
        },
        {
          label: "Credit Score Report (Credit Limit request time)",
          label: t("domesticcustomer.creditscorereport"),
          name: "creditScore",
          id: 23,
          required: true,
        },
      ]
      : [
        {
          label: "Official Id (Ine, Passport, Actual)",
          label: t("domesticcustomer.OfficialId"),
          name: "Cust_Official_Id",
          required: true,
          id: 9,
        },
        {
          label:
            "Address Proof(Official, Less Than 3 Months Of Aging), Any Of Services Invoices (Utility Bills), Bank Statement)",
          label: t("domesticcustomer.AddressProof"),
          name: "Cust_Address_Proof",
          id: 10,
          required: true,
        },
        {
          label: "Legal Representative Official ID",
          label: t("domesticcustomer.LegalRepresentativeOfficialID"),
          name: "Cust_Legal_Representative_Oficial_Id",
          id: 13,
          required: true,
        },
        {
          label: "Incorporation Act & Bylaws",
          label: t("domesticcustomer.IncorporationActBylaws"),
          name: "Cust_Incorporation_Act_&_Bylaws",
          id: 22,
          required: true,
        },
        {
          label:
            "Bank statement of accout (only the Customer data not the details of movement and also customer can erase balance) Credit Limit request time",
          label: t("domesticcustomer.Bankstatementofaccount"),
          name: "Cust_Bank_statement",
          id: 15,
          required: true,
        },
        {
          label: "Tax ID Document",
          label: t("domesticcustomer.taxiddocument"),
          name: "Cust_Tax_ID_document",
          id: 16,
          required: true,
        },
        {
          label: "Credit Score Report (Credit Limit request time)",
          label: t("domesticcustomer.creditscorereport"),
          name: "Credit_Score_Report",
          id: 23,
          required: true,
        },
        {
          label: "Power of Legal Representative",
          label: t("domesticcustomer.PowerOfLegalRepresentative"),
          name: "Cust_Power_of_the_legal_representative",
          id: 20,
          required: true,
        },
      ];
  } else {
    return customerCategory === CommonEnums.INDIVIDUAL
      ? [
        {
          label: t("domesticcustomer.OfficialId"),
          name: "Cust_Official_Id",
          required: true,
          id: 9,
        },
        {
          label: t("domesticcustomer.AddressProof"),
          name: "Cust_Address_Proof",
          id: 10,
          required: true,
        },
        {
          label: t("domesticcustomer.FiscalStatusCertificate"),
          name: "Cust_Constacia_De_Situación_Fiscal_Mexico",
          required: true,
          id: 11,
        },
        {
          label: t("domesticcustomer.Cumplimiento..."),
          name: "Cust_Cumplimiento_De_Obligaciones_Fiscales",
          id: 12,
          required: true,
        },
      ]
      : [
        {
          label: t("domesticcustomer.OfficialId"),
          name: "Cust_Official_Id",
          required: true,
          id: 9,
        },
        {
          label: t("domesticcustomer.AddressProof"),
          name: "Cust_Address_Proof",
          id: 10,
          required: true,
        },
        {
          label: t("domesticcustomer.FiscalStatusCertificate"),
          name: "Cust_Constacia_De_Situación_Fiscal_Mexico",
          required: true,
          id: 11,
        },
        {
          label: t("domesticcustomer.Cumplimiento..."),
          name: "Cust_Cumplimiento_De_Obligaciones_Fiscales",
          id: 12,
          required: true,
        },
        {
          label: t("domesticcustomer.LegalRepresentativeOfficialID"),
          name: "Cust_Legal_Representative_Oficial_Id",
          id: 13,
          required: true,
        },
        {
          label: t("domesticcustomer.CertificateofIncorporation"),
          name: "Cust_Acta_Constitutiva",
          id: 14,
          required: true,
        },
        // {
        //   label: t("domesticcustomer.Bankstatementofaccount"),
        //   name: "Cust_Bank_statement",
        //   id: 15,
        //   required: true,
        // },
        // {
        //   label: t("domesticcustomer.taxiddocument"),
        //   name: "Cust_Tax_ID_document",
        //   id: 16,
        //   required: true,
        // },
        // {
        //   label: t("domesticcustomer.creditscorereport"),
        //   name: "Credit_Score_Report",
        //   id: 23,
        //   required: true,
        // },
        // {
        //   label: t("domesticcustomer.powerofatorney"),
        //   name: "Cust_Power_of_atorney",
        //   id: 17,
        //   required: true,
        // },
        // {
        //   label: t("domesticcustomer.financialstatements"),
        //   name: "Cust_Financial_statements",
        //   id: 18,
        //   required: true,
        // },
      ];
  }
};

export const creditLineDocuments = (t, isIndividual = false) => {
  const allDocuments = [
    {
      label: t("creditline.FinancialStatements"),
      name: "FinancialStatement",
      required: true,
      id: 2,
    },
    {
      label: t("creditline.PowerofAttorney..."),
      name: "RepPowerOfNoteLegal",
      required: true,
      id: 3,
    },
    // {
    //   label: t("creditline.CreditAnalysis"),
    //   name: "CreditAnalysis",
    //   required: true,
    //   id: 4,
    // },
    // {
    //   label: t("creditline.ConstitutiveAct"),
    //   name: "ConstitutiveAct",
    //   required: true,
    //   id: 5,
    // },
    {
      label: t("creditline.PromissionoryNote"),
      name: "PromissionoryNote",
      required: true,
      id: 6,
    },
    // {
    //   label: t("creditline.IdentificationAndCompGuaranty"),
    //   name: "IdentificationAndCompGuaranty",
    //   required: true,
    //   id: 7,
    // },
    // {
    //   label: t("creditline.LevelRepresentativeIdentification"),
    //   name: "LevelRepresentativeIdentification",
    //   required: true,
    //   id: 8,
    // },
    {
      label:
        "Bank statement of accout (only the Customer data not the details of movement and also customer can erase balance) Credit Limit request time",
      label: t("domesticcustomer.Bankstatementofaccount"),
      name: "Cust_Bank_statement",
      id: 15,
      required: true,
    },
    {
      label: "Tax ID Document",
      label: t("domesticcustomer.taxiddocument"),
      name: "Cust_Tax_ID_document",
      id: 16,
      required: true,
    },
    {
      label: "Credit Score Report (Credit Limit request time)",
      label: t("domesticcustomer.creditscorereport"),
      name: "Credit_Score_Report",
      id: 23,
      required: true,
    },
    // {
    //   label: "Power of Legal Representative",
    //   label: t("domesticcustomer.PowerOfLegalRepresentative"),
    //   name: "Cust_Power_of_the_legal_representative",
    //   id: 20,
    //   required: true,
    // },
  ];

  if (isIndividual) {
    return allDocuments.filter((doc) => doc.name === "PromissionoryNote");
  }

  return allDocuments;
};

export const extractAttachments = (attachments = {}) => {
  return Object.keys(attachments).reduce((acc, key) => {
    const doc = attachments[key];
    const base64String = doc?.value || "";
    const base64FileData = base64String.split(",")[1] || "";

    if (base64FileData) {
      acc.push({
        file_Type_Id: doc?.typeId,
        fileName: doc?.fileName,
        base64FileData,
      });
    }

    return acc;
  }, []);
};

// utils/getCustomerStatus.js

// Status configuration
export const statusConfig = {
  pending: {
    label: "Pending",
    icon: <FaClock className="mr-1.5" />,
    className: "bg-yellow-50 text-yellow-700 border-yellow-200",
  },
  approved: {
    label: "Approved",
    icon: <FaCheckCircle className="mr-1.5" />,
    className: "bg-green-50 text-green-700 border-green-200",
  },
  declined: {
    label: "Declined",
    icon: <FaTimesCircle className="mr-1.5" />,
    className: "bg-red-50 text-red-700 border-red-200",
  },
  onboarded: {
    label: "Onboarded",
    icon: <FaUserCheck className="mr-1.5" />,
    className: "bg-blue-50 text-blue-700 border-blue-200",
  },
};

export function getCustomerStatus({ customer, isOnboarded = false }) {
  const allStatuses = customer?.remarks || [];
  const isOnboard = customer?.isMulti_level_approved === 1;

  const hasPending = allStatuses.some((ele) => ele?.status !== 1);

  const hasDeclined = allStatuses.some((ele) => ele?.status == 0);

  let currentStatus = statusConfig.pending;

  if (hasDeclined) {
    currentStatus = statusConfig.declined;
  } else if (isOnboard) {
    currentStatus = statusConfig.onboarded;
  } else if (!hasPending) {
    currentStatus = statusConfig.approved;
  }

  return currentStatus;
}

export function getCompanyName(code = "") {
  const companyMap = {
    MX10: "JKTornel",
    MX20: "CHTornel",
  };

  // Try exact match first, then fallback to prefix match
  return companyMap[code] || "JKTornel";
}

export const userFriendlyMessage = (error) => {
  if (error?.message?.toLowerCase().includes("network")) {
    return "Network issue. Please check your connection.";
  }

  if (error?.message?.toLowerCase().includes("timeout")) {
    return "The request timed out. Please try again.";
  }

  if (error?.message?.toLowerCase().includes("unauthorized")) {
    return "Session expired. Please log in again.";
  }

  if (error?.message?.toLowerCase().includes("not found")) {
    return "Requested resource was not found.";
  }

  if (error?.message?.toLowerCase().includes("internal server")) {
    return "Something went wrong on our end. Please try again later.";
  }

  return "Oops! Something went wrong. Please try again.";
};

export const statusColors = {
  OPEN: "bg-yellow-full text-yellow-800",
  Resolved: "bg-green-100 text-green-800",
  Pending: "bg-yellow-full text-yellow-800",
  REJECTED: "bg-red-100 text-red-800",
  CLOSED: "bg-gray-100 text-gray-800",
  Applied: "bg-green-100 text-green-800",
  FullPaid: "bg-green-100 text-green-800",
};

export const statusIcons = {
  OPEN: <FiAlertCircle className="mr-1" />,
  Resolved: <FiCheckCircle className="mr-1" />,
  Pending: <FiClock className="mr-1" />,
  REJECTED: <FiXCircle className="mr-1" />,
  CLOSED: <FiCheckCircle className="mr-1" />,
  Applied: <FiCheckCircle className="mr-1" />,
  FullPaid: <FiCheckCircle className="mr-1" />,
};

export function getApproverNameByRole(userInfo, role) {
  if (!userInfo || !role) return null;

  const levels = [
    ...(Array.isArray(userInfo?.customerHierarchy)
      ? userInfo.customerHierarchy
      : []),
    ...(Array.isArray(userInfo?.customerAccessHirarchy?.[0]?.levels)
      ? userInfo.customerAccessHirarchy[0].levels
      : []),
  ];

  if (levels.length === 0) return null;

  const approver = levels.find(
    (item) => item?.approverRole?.toLowerCase() === role.toLowerCase()
  );
  return approver ? approver.approverName : null;
}
export function getApproverEmailByRole(userInfo, role) {
  if (!userInfo || !role) return null;

  const levels = [
    ...(Array.isArray(userInfo?.customerHierarchy)
      ? userInfo.customerHierarchy
      : []),
    ...(Array.isArray(userInfo?.customerAccessHirarchy?.[0]?.levels)
      ? userInfo.customerAccessHirarchy[0].levels
      : []),
  ];

  if (levels.length === 0) return null;

  const approver = levels.find(
    (item) => item?.approverRole?.toLowerCase() === role.toLowerCase()
  );
  return approver ? approver.approverMail : null;
}

export function getApproverMobileByRole(userInfo, role) {
  if (!userInfo || !role) return null;

  const levels = [
    ...(Array.isArray(userInfo?.customerHierarchy)
      ? userInfo.customerHierarchy
      : []),
    ...(Array.isArray(userInfo?.customerAccessHirarchy?.[0]?.levels)
      ? userInfo.customerAccessHirarchy[0].levels
      : []),
  ];

  if (levels.length === 0) return null;

  const approver = levels.find(
    (item) => item?.approverRole?.toLowerCase() === role.toLowerCase()
  );
  return approver ? approver.approverMobile : null;
}


function parseAccountingNumber(value) {
  if (typeof value === "string" && value.trim().endsWith("-")) {
    value = "-" + value.trim().slice(0, -1);
  }
  return parseFloat(value) || 0;
}

// Helper function for consistent borders
function allBorders() {
  return {
    top: { style: "thin" },
    bottom: { style: "thin" },
    left: { style: "thin" },
    right: { style: "thin" },
  };
}

export const  exportStyledExcel = async (
  t,
  ViewAccountStatmentData,
  userInfo
) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Account Statement");
  // Remove gridlines
  sheet.views = [{ showGridLines: false }];
  const dataToExport = [...ViewAccountStatmentData]?.reverse() || [];

  // Add logo
  const response = await fetch("/assets/img/media/JkExcelLogo.png");
  const imageBlob = await response.blob();
  const buffer = await imageBlob.arrayBuffer();
  const imageId = workbook.addImage({
    buffer,
    extension: "png",
  });

  sheet.addImage(imageId, {
    tl: { col: 0, row: 1 },
    ext: { width: 300, height: 150 },
  });

  // Add address line below logo
  const addressRow = sheet.getRow(10);
  addressRow.getCell(1).value =
    "Santa Lucia No. 311 Acayucan Delegación, Azcapotzalco, CDMX";
  addressRow.getCell(1).font = { bold: true };
  sheet.mergeCells("B10:F10");
  const statementInfo = dataToExport?.[0] || {};

  // Header info sections
  const headerInfo = [
    [
      t("viewaccountdetailsoncustomer.CUSTOMER_NO"),
      userInfo?.username || "N/A",
    ],
    [
      t("viewaccountdetailsoncustomer.CUSTOMER_NAME"),
      userInfo?.firstName || "N/A",
    ],
    [
      t("viewaccountdetailsoncustomer.ADDRESS"),
      statementInfo?.AccAddress || "N/A",
    ],
  ];
  const contactInfo = [
    [
      t("viewaccountdetailsoncustomer.SALES_MANAGER"),
      getApproverNameByRole(userInfo, UserRoles?.SALES_MANAGER) || "N/A",
    ],
    [
      t("viewaccountdetailsoncustomer.CUSTOMER_SERVICE"),
      getApproverNameByRole(userInfo, UserRoles?.CUSTOMER_SERVICE) || "N/A",
    ],
    [
      t("viewaccountdetailsoncustomer.CREDIT_SUPERVISOR"),
      getApproverNameByRole(userInfo, UserRoles?.CCSUPERVISOR) || "N/A",
    ],
  ];
  
const formatAmountWithCurrency = (amount) => {
  const SPACE = "\u00A0\u00A0";
  const currency = statementInfo?.AccCurrency || "MXN";
  return `${formatCurrency(parseAccountingNumber(amount))}${SPACE}${currency}`;
};

const availableCreditValue = formatCurrency(
  parseAccountingNumber(statementInfo?.AccCredit) - parseAccountingNumber(statementInfo?.AccOut)
);
const SPACE = "\u00A0\u00A0"; // two non-breaking spaces

const availableCreditWithCurrency = `${availableCreditValue}${SPACE}${statementInfo?.AccCurrency || "MXN"}`;
const creditline = `${formatCurrency(statementInfo?.AccCredit)}${SPACE}${statementInfo?.AccCurrency || "MXN"}`
  const financialSummary = [
    [
      t("viewaccountdetailsoncustomer.Credit_Line"),
     creditline || "N/A",
    ],
    [
      t("viewaccountdetailsoncustomer.Outstanding"),
     formatAmountWithCurrency(statementInfo?.AccOut) || "N/A",
    ],
    [
      t("viewaccountdetailsoncustomer.Available"),
      availableCreditWithCurrency || "N/A",
    ],
    [
      t("customerdashboard.pastdue"),
      formatAmountWithCurrency(statementInfo?.AccPastdue) || "N/A",
      // formatCurrency(statementInfo?.AccDue) || "N/A",
    ],
    [
      t("customerdashboard.PromissoryNote"),
      formatCurrency(statementInfo?.AccGuarantee) || "N/A",
    ],
    ["Mortgage", userInfo?.typeOfGuarantee || "N/A"],
  ];

  const startRow = 3;

  headerInfo.forEach(([label, value], index) => {
    const rowIdx = startRow + index;
    const labelCell = sheet.getCell(`C${rowIdx}`);
    const valueCell = sheet.getCell(`D${rowIdx}`);
    labelCell.value = label;
    labelCell.font = { bold: true, color: { argb: "FF0000" } };
    valueCell.value = value;
    labelCell.border = allBorders();
    valueCell.border = allBorders();
    valueCell.alignment = { horizontal: "left" };
  });

  contactInfo.forEach(([label, value], index) => {
    const rowIdx = 7 + index;
    const labelCell = sheet.getCell(`C${rowIdx}`);
    const valueCell = sheet.getCell(`D${rowIdx}`);
    labelCell.value = label;
    labelCell.border = allBorders();
    valueCell.border = allBorders();
    labelCell.font = { bold: true };
    valueCell.value = value;
  });

  financialSummary.forEach(([label, value], index) => {
    const rowIdx = startRow + index;
    const labelCell = sheet.getCell(`F${rowIdx}`);
    const valueCell = sheet.getCell(`G${rowIdx}`);
    labelCell.value = label;
    valueCell.value = value;
    labelCell.border = allBorders();
    valueCell.border = allBorders();
    valueCell.font = {
      bold: label === "Total Due",
      color: label === "Total Due" ? { argb: "FF0000" } : undefined,
    };
  });

  // Add a gap row before table
  const gapRowIndex =
    7 +
    Math.max(headerInfo.length, contactInfo.length, financialSummary.length) +
    1;

  // Table headers
  const isNotMexico = userInfo?.regionName?.toLowerCase() !== CommonEnums.MEXICO?.toLowerCase();
  const tableHeaders = [
    t("viewaccountdetailsoncustomer.invoicenumberS"),
    t("viewaccountdetailsoncustomer.Assignment"),
    t("viewaccountdetailsoncustomer.DocumentType"),
    t("viewaccountdetailsoncustomer.invoicedate"),
    isNotMexico ? "ETA" : t("viewaccountdetailsoncustomer.dateofdelivery"),
    t("viewaccountdetailsoncustomer.SaleOrder"),
    ...(isNotMexico ? [t("viewaccountdetailsoncustomer.BillofLading")] : []),
    t("viewaccountdetailsoncustomer.Currency"),
    t("viewaccountdetailsoncustomer.DocumentAmount"),
    t("viewaccountdetailsoncustomer.Balance"),
    t("viewaccountdetailsoncustomer.duedate"),
    t("viewaccountdetailsoncustomer.Aging"),
  ];

  const headerRow = sheet.getRow(gapRowIndex);
  tableHeaders.forEach((header, idx) => {
    const cell = headerRow.getCell(idx + 1);
    cell.value = header;
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "800080" }, // Purple
    };
    cell.font = { color: { argb: "FFFFFF" }, bold: true };
    cell.alignment = { horizontal: "center" };
  });

  // Data rows
  dataToExport.forEach((item, i) => {
    const row = sheet.getRow(gapRowIndex + 1 + i);
    const values = [
      item.FiscalFolio ?? "",
      item.AccAssignment ?? "",
      getDocumentTypeLabel(item.AccDocType, t) ?? "",
      formatDate(item.AccDate) ?? "",
      formatDate(item?.AccDelivery) ?? "",
      item.AccSalesOd ?? "",
      ...(isNotMexico ? [item.AccBl ?? ""] : []),
      item.AccCurrency ?? "",
      formatCurrency(item.AccDocamount) ?? "",
      formatCurrency(item.AccDocBal) ?? "",
      formatDate(item.AccDueDate) ?? "",
      item.AccArrear ?? "",
    ];
    values.forEach((val, colIdx) => {
      const cell = row.getCell(colIdx + 1);
      cell.value = val;
      cell.alignment = { horizontal: "center" };

      // Style Document Status
      if (colIdx === 11) {
        if (val === "Due") {
          cell.font = { color: { argb: "FF0000" }, bold: true };
        } else if (val === "Not Due") {
          cell.font = { color: { argb: "008000" }, bold: true };
        }
      }

      // Apply borders only to data cells
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });
  });

  // Auto-width for columns
  sheet.columns.forEach((col) => {
    let maxLength = 10;
    col.eachCell({ includeEmpty: true }, (cell) => {
      const value = cell.value ? cell.value.toString() : "";
      maxLength = Math.max(maxLength, value.length + 5);
    });
    col.width = maxLength;
  });

  // Save file
  const excelBuffer = await workbook.xlsx.writeBuffer();
  const now = new Date();

  const formattedDate = `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  let hours = now.getHours();
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12 || 12; // convert to 12-hour format
  const formattedTime = `${String(hours).padStart(2, "0")}-${minutes}${ampm}`;

  const filename = `Account_Statement_${userInfo?.username || userInfo?.accountNumber
    }_${formattedDate}_${formattedTime}.xlsx`;

  saveAs(new Blob([excelBuffer]), filename);
};

export const exportCustomerPaymentExcel = async (
  t,
  customerPaymentData,
  user
) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Customer Payments");

  // Disable gridlines
  sheet.views = [{ showGridLines: false }];

  // Add logo (if needed)
  const response = await fetch("/assets/img/media/JkExcelLogo.png");
  const imageBlob = await response.blob();
  const buffer = await imageBlob.arrayBuffer();
  const imageId = workbook.addImage({
    buffer,
    extension: "png",
  });

  sheet.addImage(imageId, {
    tl: { col: 0, row: 1 },
    ext: { width: 300, height: 150 },
  });

  // Optional: Add a title
  const titleRow = sheet.getRow(10);
  titleRow.getCell(2).value = t("common.CustomerPaymentReport");
  titleRow.getCell(2).font = { size: 16, bold: true };
  sheet.mergeCells("B10:G10");

  const tableHeaders = [
    t("common.paymentdate"),
    t("common.currency"),
    // t("common.paymentstatus"),
    t("common.paymentamount"),
    t("common.paymentreference"),
  ];

  const headerRowIndex = 12;
  const headerRow = sheet.getRow(headerRowIndex);

  tableHeaders.forEach((header, idx) => {
    const cell = headerRow.getCell(idx + 1);
    cell.value = header;
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "800080" }, // purple
    };
    cell.font = { bold: true, color: { argb: "FFFFFF" } };
    cell.alignment = { horizontal: "center" };
    cell.border = allBorders();
  });

  const dataToExport = [...customerPaymentData] || [];

  dataToExport.forEach((item, i) => {
    const row = sheet.getRow(headerRowIndex + 1 + i);
    const date = item.PayDate
      ? formatDate(
        new Date(parseInt(item.PayDate.replace(/[^0-9]/g, ""))),
        true
      )
      : "";

    const values = [
      date,
      item.Currency || "",
      // item.CustStatus || "",
      parseFloat(item.PayAmt?.trim()) || 0,
      item.PayRef || "",
    ];

    values.forEach((val, colIdx) => {
      const cell = row.getCell(colIdx + 1);
      cell.value = val;
      cell.border = allBorders();
      cell.alignment = { horizontal: "center" };

      if (colIdx === 2 && val === "Not Paid") {
        // Payment Status column
        cell.alignment = { horizontal: "center" };

        cell.font = { color: { argb: "FF0000" }, bold: true };
      }

      if (colIdx === 3) {
        // Payment Amount column
        cell.alignment = { horizontal: "center" };
        cell.numFmt = '"$"#,##0.00;[Red]-"$"#,##0.00';
      }
    });
  });

  // Auto-resize
  sheet.columns.forEach((column) => {
    let maxLength = 10;
    column.eachCell({ includeEmpty: true }, (cell) => {
      const cellValue = cell.value ? cell.value.toString() : "";
      maxLength = Math.max(maxLength, cellValue.length + 2);
    });
    column.width = maxLength;
  });

  const excelBuffer = await workbook.xlsx.writeBuffer();
  const now = new Date();
  // const formattedDate = now.toLocaleString().split("T")[0];
  // const formattedTime = `${now.getHours()}-${now.getMinutes()}${
  //   now.getHours() >= 12 ? "PM" : "AM"
  // }`;

  const formattedDate = `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  let hours = now.getHours();
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12 || 12; // convert to 12-hour format
  const formattedTime = `${String(hours).padStart(2, "0")}-${minutes}${ampm}`;

  const filename = `Customer_Payment_Report_${user?.username || user?.accountNumber
    }_${formattedDate}_${formattedTime}.xlsx`;

  saveAs(new Blob([excelBuffer]), filename);
};



export const getMXNtoUSDRate = async () => {
  try {
    const res = await fetch("https://open.er-api.com/v6/latest/MXN");
    const data = await res.json();

    return Number(data?.rates?.USD) || 1;
  } catch (error) {
    console.error("FX API ERROR:", error);
    return 1;
  }
};




export const exportCustomerPriceList = async (
  t,
  priceListData,
  customerInfo
) => {
  try {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Customer Price List");
    // Remove gridlines
    sheet.views = [{ showGridLines: false }];
    const dataToExport = Array.isArray(priceListData) ? [...priceListData] : [];

    // Add logo
    try {
      const response = await fetch("/assets/img/media/JkExcelLogo.png");
      if (!response.ok) throw new Error("Failed to fetch logo");
      const imageBlob = await response.blob();
      const buffer = await imageBlob.arrayBuffer();
      const imageId = workbook.addImage({
        buffer,
        extension: "png",
      });

      sheet.addImage(imageId, {
        tl: { col: 0, row: 1 },
        ext: { width: 200, height: 100 },
      });
    } catch (error) {
      console.error("Error loading logo:", error);
      // Continue without logo if there's an error
    }

    // Add address line below logo
    const addressRow = sheet.getRow(7);
    addressRow.getCell(1).value =
      "Santa Lucia No. 311 Acayucan Delegación, Azcapotzalco, CDMX";
    addressRow.getCell(1).font = { bold: true };
    sheet.mergeCells("A10:F10");

    // Header info sections
    const headerInfo = [
      [
        t("customerPriceList.CUSTOMER_NO", "CUSTOMER NO"),
        customerInfo?.username || customerInfo?.accountNumber || "N/A",
      ],
      [
        t("customerPriceList.CUSTOMER_NAME", "CUSTOMER NAME"),
        customerInfo?.accountName || "N/A",
      ],
      [
        t("customerPriceList.DATE_GENERATED", "DATE GENERATED"),
        formatDate(new Date()),
      ],
    ];

    const startRow = 7; // Start after logo and address

    headerInfo.forEach(([label, value], index) => {
      const rowIdx = startRow + index;
      const labelCell = sheet.getCell(`D${rowIdx}`);
      const valueCell = sheet.getCell(`E${rowIdx}`);
      labelCell.value = label;
      labelCell.font = { bold: true, color: { argb: "FF0000" } };
      valueCell.value = value;
      labelCell.border = allBorders();
      valueCell.border = allBorders();
      valueCell.alignment = { horizontal: "left" };
    });

    // Add a gap row before table
    const gapRowIndex = startRow + headerInfo.length + 2;

    // Main table headers - using actual data field names
    const headers = [
      t("common.EXTERNALMATERIALCODE"),
      t("common.SKU"),
      t("common.DESCRIPTION"),
      t("common.CURRENCY"),
      t("common.TOTALINVOICED"),
      t("common.QUANTITYINVOICED"),
      t("common.UNITPRICE"),
    ];

    // Add headers
    const headerRow = sheet.getRow(gapRowIndex);
    headerRow.height = 25;

    headers.forEach((header, idx) => {
      const cell = headerRow.getCell(idx + 1);
      cell.value = header;
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FEEE00" }, // Yellow
      };
      cell.font = { bold: true };
      cell.alignment = { horizontal: "center" };
      cell.border = allBorders();
    });

    // Data rows
    dataToExport.forEach((item, rowIndex) => {
      const row = sheet.getRow(gapRowIndex + 1 + rowIndex); // +1 to start after header
      const currency = CURRENCY_OPTIONS.find(
        (c) => c.value?.toLowerCase() === item?.Currency?.toLowerCase()
      );
      // Map data fields to columns - using proper field names from your data
      const rowData = [
        item.ExtMat || "-",
        item.Sku || "-",
        item.Description || "-",
        currency?.symbol && currency?.value
          ? `${currency?.symbol} ${currency?.value}`
          : item.Currency || "-",
        item.TotalInv ? formatCurrency(item.TotalInv) : "-",
        item.QtyInv || "-",
        item.TotalInv && item.QtyInv
          ? formatCurrency(item.TotalInv / item.QtyInv)
          : "-",
      ];

      rowData.forEach((value, colIndex) => {
        const cell = row.getCell(colIndex + 1);
        cell.value = value;
        cell.border = allBorders();

        // Center align all except description
        cell.alignment = {
          horizontal: colIndex === 2 ? "left" : "center",
          vertical: "middle",
        };
      });
    });

    // Auto-width for columns with limits
    sheet.columns.forEach((col, colIndex) => {
      let maxLength = 15; // Minimum width
      col.eachCell({ includeEmpty: true }, (cell) => {
        const value = cell.value ? cell.value.toString() : "";
        maxLength = Math.max(maxLength, value.length + 2);
      });

      // Set different widths for different columns
      if (colIndex === 3) {
        // Description column
        col.width = Math.min(Math.max(maxLength, 30), 60);
      } else if ([4, 6].includes(colIndex)) {
        // Numeric columns
        col.width = Math.max(maxLength, 15);
      } else {
        col.width = Math.min(maxLength, 30);
      }
    });

    // Save file
    const excelBuffer = await workbook.xlsx.writeBuffer();
    const now = new Date();

    const formattedDate = `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12; // convert to 12-hour format

    const formattedTime = `${String(hours).padStart(2, "0")}-${minutes}${ampm}`;

    const filename = `Customer_Price_List_${customerInfo?.username || customerInfo?.accountNumber || "Unknown"
      }_${formattedDate}_${formattedTime}.xlsx`;

    saveAs(new Blob([excelBuffer]), filename);
  } catch (error) {
    console.error("Error exporting price list:", error);
    throw error;
  }
};

export const exportCustomerBackOrders = async (
  t,
  backOrderData,
  customerInfo
) => {
  try {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Customer Back Orders");

    // Remove gridlines
    sheet.views = [{ showGridLines: false }];
    const dataToExport = Array.isArray(backOrderData) ? [...backOrderData] : [];

    // Add logo
    try {
      const response = await fetch("/assets/img/media/JkExcelLogo.png");
      if (!response.ok) throw new Error("Failed to fetch logo");
      const imageBlob = await response.blob();
      const buffer = await imageBlob.arrayBuffer();
      const imageId = workbook.addImage({
        buffer,
        extension: "png",
      });

      sheet.addImage(imageId, {
        tl: { col: 0, row: 1 },
        ext: { width: 200, height: 100 },
      });
    } catch (error) {
      console.error("Error loading logo:", error);
      // Continue without logo if there's an error
    }

    // Add address line below logo
    const addressRow = sheet.getRow(7);
    addressRow.getCell(1).value =
      "Santa Lucia No. 311 Acayucan Delegación, Azcapotzalco, CDMX";
    addressRow.getCell(1).font = { bold: true };
    sheet.mergeCells("A7:F7"); // Changed from B10:F10 to A10:F10 for better alignment

    // Header info sections
    const headerInfo = [
      [
        t("customerPriceList.CUSTOMER_NO", "CUSTOMER NO"),
        customerInfo?.username || customerInfo?.accountNumber || "N/A",
      ],
      [
        t("customerPriceList.CUSTOMER_NAME", "CUSTOMER NAME"),
        customerInfo?.accountName || "N/A",
      ],
      [
        t("customerPriceList.DATE_GENERATED", "GENERATED DATE"),
        formatDate(new Date()),
      ],
    ];

    const startRow = 3; // Increased from 3 to 12 to account for logo and address

    headerInfo.forEach(([label, value], index) => {
      const rowIdx = startRow + index;
      const labelCell = sheet.getCell(`C${rowIdx}`);
      const valueCell = sheet.getCell(`D${rowIdx}`);
      labelCell.value = label;
      labelCell.font = { bold: true, color: { argb: "FF0000" } };
      valueCell.value = value;
      labelCell.border = allBorders();
      valueCell.border = allBorders();
      valueCell.alignment = { horizontal: "left" };
    });

    // Add a gap row before table
    const gapRowIndex = startRow + headerInfo.length + 2;

    // Main table headers
    const mainHeaders = [
      t("common.EXTERNALMATERIALCODE"),
      t("common.SKU"),
      t("common.DESCRIPTION"),
      t("common.SALESORDERSQTY"),
      t("common.PGISQTY"),
      t("common.CURRENCY"),
      t("common.TOTAL"),
    ];

    // Add main headers
    const headerRow = sheet.getRow(gapRowIndex);
    headerRow.height = 25;

    // Add sub headers
    const subHeaderRow = sheet.getRow(gapRowIndex + 1);
    mainHeaders.forEach((header, idx) => {
      const cell = subHeaderRow.getCell(idx + 1);
      cell.value = header;
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FEEE00" }, // Yellow
      };
      cell.font = { bold: true };
      cell.alignment = { horizontal: "center" };
      cell.border = allBorders();
    });

    // Data rows
    dataToExport.forEach((item, i) => {
      const row = sheet.getRow(gapRowIndex + 2 + i);
      const currency = CURRENCY_OPTIONS.find(
        (c) => c.value?.toLowerCase() === item?.Currency?.toLowerCase()
      );
      // Basic info
      row.getCell(1).value = item.ExtMat || "";
      row.getCell(2).value = item.Sku || "";
      row.getCell(3).value = item.Description || "";
      row.getCell(4).value = item.SaleOrderQty || 0;
      row.getCell(5).value = item.PgisQty || 0;
      row.getCell(6).value =
        currency?.symbol && currency?.value
          ? `${currency?.symbol} ${currency?.value}`
          : item.Currency || "-";
      row.getCell(7).value = item.Total;

      // Apply borders to all cells
      for (let col = 1; col <= mainHeaders.length; col++) {
        const cell = row.getCell(col);
        cell.border = allBorders();
        cell.alignment = { horizontal: "center" };
      }
    });

    // Auto-width for columns with minimum width
    sheet.columns.forEach((col) => {
      let maxLength = 15; // Set minimum width to 15
      col.eachCell({ includeEmpty: true }, (cell) => {
        const value = cell.value ? cell.value.toString() : "";
        maxLength = Math.max(maxLength, value.length + 2);
      });
      col.width = Math.min(maxLength, 50); // Set maximum width to 50
    });

    // Save file
    const excelBuffer = await workbook.xlsx.writeBuffer();
    const now = new Date();

    const formattedDate = `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12; // convert to 12-hour format

    const formattedTime = `${String(hours).padStart(2, "0")}-${minutes}${ampm}`;

    const filename = `Customer_Back_Orders_${customerInfo?.username || customerInfo?.accountNumber || "Unknown"
      }_${formattedDate}_${formattedTime}.xlsx`;

    saveAs(new Blob([excelBuffer]), filename);
  } catch (error) {
    console.error("Error exporting back orders:", error);
    throw error; // Re-throw the error for the caller to handle
  }
};

export function formatCompactDateToLocalString(compactDate) {
  if (!compactDate) return "";

  // Handle Microsoft-style \/Date(1750377600000)\/
  const msDateMatch = compactDate.toString().match(/\/Date\((\d+)\)\//);
  if (msDateMatch) {
    const timestamp = parseInt(msDateMatch[1], 10);
    const date = new Date(timestamp);
    return date.toLocaleString(); // or use toLocaleString() if local format is needed
  }

  // Handle compact date like 20220105
  const dateStr = compactDate.toString();
  if (dateStr.length === 8 && /^\d{8}$/.test(dateStr)) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${year}-${month}-${day}`;
  }

  // If input doesn't match known formats, return as-is
  return compactDate;
}

// utils/getBusinessUnitName.js
export const getBusinessUnitName = (id, businessUnitData) => {
  if (!id || !Array.isArray(businessUnitData)) return "";

  const unit = businessUnitData.find((bu) => bu.id === id);
  return unit ? unit.name : "";
};

export const allowedRolesForDispute = [
  UserRoles?.LOGISTIC_SUPERVISOR?.toLowerCase(),
  UserRoles?.LOGISTIC_MANAGER?.toLowerCase(),
  UserRoles?.CCSUPERVISOR?.toLowerCase(),
  UserRoles?.CUSTOMER_SERVICE?.toLowerCase(),
  UserRoles?.CC_MANAGER?.toLowerCase(),
  UserRoles?.CFO?.toLowerCase(),
  UserRoles?.CEO?.toLowerCase(),
  UserRoles?.SALES_MANAGER?.toLowerCase(),
  UserRoles?.VP_SALES?.toLowerCase(),
  UserRoles?.LOGISTIC_RESPONSIBLE?.toLowerCase(),
];

export const getDepartmentIcon = (code) => {
  switch (code.toLowerCase()) {
    case "logisticresponsible":
      return <FaTruck className="text-blue-500 mr-2" />;
    case "ccsupervisor":
      return <FaMoneyCheckAlt className="text-green-600 mr-2" />;
    case "customerservice":
      return <FaHeadset className="text-purple-500 mr-2" />;
    default:
      return null;
  }
};
// utils/getDocumentTypeLabel.js
export const getDocumentTypeLabel = (docType, t) => {
  const docTypeMap = {
    RV: t("docTypes.Invoice"),
    RC: t("docTypes.CreditNote"),
    ZD: t("docTypes.DebitNote"),
    AB: t("docTypes.PaymentAdjustment"),
    AC: t("docTypes.AdvanceApplied"),
    UR: t("docTypes.PaymentDone"),
    IG: t("docTypes.Advancepayment"),
    DZ: t("docTypes.Paymentrecieved"),
    ZY: t("docTypes.TransactionCancelled"),
  };

  return docTypeMap[docType] || docType || "-";
};
export const departmentOptions = (t) => [
  { label: t("raiseddisputeform.invoice"), value: 1 },
  { label: t("raiseddisputeform.Payments"), value: 2 },
  { label: t("raiseddisputeform.Logistics"), value: 3 },
  { label: "Other", value: 4 },
];

export const getDashboardPathForRole = (role) => {
  // Normalize the role to match our enum keys
  const normalizedRole =
    Object.values(UserRoles).find(
      (validRole) => validRole.toLowerCase() === role?.toLowerCase()
    ) || UserRoles.DEFAULT;

  switch (normalizedRole) {
    case UserRoles.ADMIN:
      return Paths.EXECUTIVE_SUMMARY;
    case UserRoles.SALES_MANAGER:
      return Paths.SELLER_DASHBOARD;
    case UserRoles.REGIONAL_MANAGER:
      return Paths.SALES_MANAGER_DASHBOARD;
    case UserRoles.MARKETING_HEAD:
      return Paths.SELLER_DASHBOARD;
    case UserRoles.CUSTOMER_SERVICE:
      return Paths.CUSTOMER_SERVICE_DASHBOARD;
    case UserRoles.VP_SALES:
      return Paths.SALES_MANAGER_DASHBOARD;
    case UserRoles.CFO:
      return Paths.EXECUTIVE_SUMMARY;
    case UserRoles.CCSUPERVISOR:
      return Paths.CC_SUPERVISOR_DASHBOARD;
    case UserRoles.CUSTOMER:
      return Paths.CUSTOMER_DASHBOARD;
    case UserRoles.LOGISTIC_MANAGER:
    case UserRoles.LOGISTIC_SUPERVISOR:
    case UserRoles.LOGISTIC_RESPONSIBLE:
      return Paths.LOGISTIC_SUPERVISOR_DASHBOARD;
    case UserRoles.CEO:
      return Paths.EXECUTIVE_SUMMARY;
    case UserRoles.CC_MANAGER:
      return Paths.CC_MANAGER_DASHBOARD;
    case UserRoles.MARKETING_MANAGER:
      return Paths.MARKETINGMANAGER_DASHBOARD;
    default:
      return Paths.UNKNOWN_ROLE;
  }
};

// Utility to convert 'YYYYMMDD' to 'YYYY-MM-DD' for HTML date input
export const toHtmlDate = (dateStr) => {
  if (!dateStr) return "";
  if (/^\d{8}$/.test(dateStr)) {
    return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(
      6,
      8
    )}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  // Try to parse ISO or other formats
  const d = new Date(dateStr);
  if (!isNaN(d)) {
    return d.toISOString().slice(0, 10);
  }
  return "";
};

export const addSpaceBetweenCaps = (text) => {
  if (!text) return "";
  return text.replace(/([a-z])([A-Z])/g, "$1 $2"); // insert space before every capital
};

export const iconMap = {
  // Public
  [Paths.ROOT]: Layout,
  [Paths.ADD_CUSTOMER]: PlusSquare,
  [Paths.UPDATE_CUSTOMER]: Edit,
  [Paths.REGISTRATION_SUCCESS]: CheckCircle,
  [Paths.WILDCARD]: HelpCircle,

  // Dashboards
  [Paths.EXECUTIVE_SUMMARY]: BarChart2,
  [Paths.SELLER_DASHBOARD]: Layout,
  [Paths.SALES_MANAGER_DASHBOARD]: Layout,
  [Paths.CUSTOMER_SERVICE_DASHBOARD]: Layout,
  [Paths.CUSTOMER_DASHBOARD]: Layout,
  [Paths.CC_SUPERVISOR_DASHBOARD]: Layout,
  [Paths.LOGISTIC_SUPERVISOR_DASHBOARD]: Layout,
  [Paths.EXECUTIVE_DASHBOARD]: Layout,
  [Paths.CC_MANAGER_DASHBOARD]: Layout,
  [Paths.ADMIN_DASHBOARD]: Layout,
  [Paths.MARKETINGMANAGER_DASHBOARD]: Target,

  // Customer
  [Paths.ONBOARDED_CUSTOMERS]: Users,
  [Paths.APPROVAL_CUSTOMER]: UserCheck,
  [Paths.CUSTOMER_REGION]: Globe,
  [Paths.SUBCUSTOMER_REGION]: FaNetworkWired,
  [Paths.CUSTOMER_TYPE]: File,
  [Paths.CUSTOMERS_HERARCHICAL]: Layers,
  [Paths.CUSTOMER_PRICE_LIST]: IoPricetag,
  [Paths.CUSTOMER_BACK_ORDER]: Package,
  [Paths.CUSTOMER_HERARCHY_LEVEL]: BiNetworkChart,

  // Invoices
  [Paths.INVOICES]: FileText,

  // Payments
  [Paths.CUSTOMER_PAYMENTS]: CreditCard,
  [Paths.PAYMENTS]: CreditCard,
  [Paths.PAYMENT_CUSTOMER]: BiReceipt,

  // Logistics
  [Paths.LOGISTIC_CUSTOMER]: Package,
  [Paths.LOGISTIC]: Package,

  // Disputes
  [Paths.RAISED_DISPUTE]: AlertCircle,
  [Paths.DISPUTE_DETAILS]: AlertCircle,
  [Paths.DISPUTE_LEVEL_ASSIGNMENT]: List,

  // Account Statements
  [Paths.VIEW_ACCOUNT_STATEMENT]: FileText,
  [Paths.CUSTOMER_ACCOUNT_STATEMENT]: FaFileSignature,

  // Management
  [Paths.BUSINESS_UNIT]: Database,
  [Paths.ROLE]: User,
  [Paths.DEPARTMENT]: Briefcase,
  [Paths.DESIGNATION]: Settings,
  [Paths.USERS]: Users,
  [Paths.APPROVAL_LEVEL_ASSIGNMENT]: Clipboard,
  [Paths.BULK_UPLOAD]: Upload,

  // Credit
  [Paths.CREDIT_LINE]: CreditCard,
  [Paths.CREDIT_LINE_REQUEST]: CreditCard,

  // Profile & Guides
  [Paths.MY_PROFILE]: BiUserCircle,
  [Paths.USER_GUIDE]: Book,

  // Feedback
  [Paths.CUSTOMER_FEEDBACK]: MessageSquare,

  // Fallback
  [Paths.UNKNOWN_ROLE]: TbShieldQuestion,
  [Paths.VIEW_ACCOUNT_STATMENT]: FileText,
};

// Generate currency options from country-data
export const CURRENCY_OPTIONS = Object.values(currencies)
  .filter((currency) => currency.code && currency.name) // Filter out incomplete entries
  .map((currency) => ({
    value: currency.code,
    label: currency.name,
    symbol: currency.symbol || currency.code, // Fallback to code if no symbol
    // Note: country-data doesn't include flags, you might want to add a flag mapping
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

// If you want to add flags, you can create a mapping:
const CURRENCY_FLAG_MAP = {
  USD: "🇺🇸",
  EUR: "🇪🇺",
  GBP: "🇬🇧",
  JPY: "🇯🇵",
  AUD: "🇦🇺",
  CAD: "🇨🇦",
  CHF: "🇨🇭",
  CNY: "🇨🇳",
  INR: "🇮🇳",
  KRW: "🇰🇷",
  BRL: "🇧🇷",
  MXN: "🇲🇽",
  SGD: "🇸🇬",
  HKD: "🇭🇰",
  ZAR: "🇿🇦",
  RUB: "🇷🇺",
  // Add more as needed
};

// Enhanced version with flags
export const CURRENCY_OPTIONS_WITH_FLAGS = Object.values(currencies)
  .filter((currency) => currency.code && currency.name)
  .map((currency) => ({
    value: currency.code,
    label: currency.name,
    symbol: currency.symbol || currency.code,
    flag: CURRENCY_FLAG_MAP[currency.code] || "🏳️", // Default flag if not mapped
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

// Alternative: If you want to limit to major currencies only
const MAJOR_CURRENCIES = [
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "AUD",
  "CAD",
  "CHF",
  "CNY",
  "INR",
  "KRW",
  "BRL",
  "MXN",
  "SGD",
  "HKD",
  "ZAR",
  "RUB",
];

export const MAJOR_CURRENCY_OPTIONS = Object.values(currencies)
  .filter(
    (currency) =>
      currency.code && currency.name && MAJOR_CURRENCIES.includes(currency.code)
  )
  .map((currency) => ({
    value: currency.code,
    label: currency.name,
    symbol: currency.symbol || currency.code,
    flag: CURRENCY_FLAG_MAP[currency.code] || "🏳️",
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

// utils/currency.js
export const getCurrencyMeta = (currencyCode) => {
  if (!currencyCode) return { symbol: "", value: "" };

  const currency = CURRENCY_OPTIONS.find(
    (c) => c.value?.toLowerCase() === currencyCode?.toLowerCase()
  );

  return {
    symbol: currency?.symbol || "",
    value: currency?.value || currencyCode || "",
  };
};

// Format time to MM:SS
export const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
    .toString()
    .padStart(2, "0")}`;
};
